import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

type Theme = 'light' | 'dark'

// ── CSS ─────────────────────────────────────────────────────────────────────

const TPL_CSS = `
#tpl-root{--ink:#0f1016;--muted:#636672;--faint:#9a9caa;--line:#e8e9f0;--bg:#f7f7fa;--paper:#ffffff;--accent:#6366f1;--accent-2:#4f46e5;--soft:#eef0ff;--fb:'Inter',-apple-system,'Segoe UI',sans-serif;--fd:'Inter Tight',-apple-system,'Segoe UI',sans-serif}
#tpl-root[data-theme="dark"]{--ink:#e8eaf6;--muted:#888ba8;--faint:#454660;--line:#252640;--bg:#111220;--paper:#18192c;--accent:#6366f1;--accent-2:#818cf8;--soft:#1c1e40}

#tpl-root *{margin:0;padding:0;box-sizing:border-box}
#tpl-root{font-family:var(--fb);font-size:15px;line-height:1.6;color:var(--ink);background:var(--bg);-webkit-font-smoothing:antialiased;min-height:100vh}
#tpl-root a{text-decoration:none;color:inherit}
#tpl-root button{font-family:inherit;border:0;background:none;cursor:pointer;color:inherit}
#tpl-root :focus-visible{outline:2px solid var(--accent);outline-offset:3px;border-radius:6px}
#tpl-root ::selection{background:var(--accent);color:#fff}
#tpl-root .container{max-width:1160px;margin:0 auto;padding:0 24px}

/* header */
#tpl-root header{position:sticky;top:0;z-index:60;background:rgba(255,255,255,.86);-webkit-backdrop-filter:blur(14px);backdrop-filter:blur(14px);border-bottom:1px solid transparent;transition:border-color .3s,background .3s}
#tpl-root header.scrolled{border-color:var(--line);box-shadow:0 4px 20px rgba(15,16,40,.06)}
#tpl-root[data-theme="dark"] header{background:rgba(17,18,32,.9)}
#tpl-root[data-theme="dark"] header.scrolled{box-shadow:0 4px 20px rgba(0,0,0,.3)}
#tpl-root .h-in{display:flex;align-items:center;gap:12px;height:62px}
#tpl-root .brand{display:flex;align-items:center;gap:8px;cursor:pointer}
#tpl-root .brand-mark{width:26px;height:26px;border-radius:8px;background:var(--accent);color:#fff;font-size:12px;font-weight:800;display:grid;place-items:center;flex-shrink:0}
#tpl-root .brand-name{font-family:var(--fd);font-weight:800;font-size:15px;letter-spacing:-.02em;color:var(--ink)}
#tpl-root .sep{color:var(--line);font-size:18px;margin:0 2px}
#tpl-root .crumb{font-size:13px;color:var(--muted);font-weight:500}
#tpl-root .h-right{margin-left:auto;display:flex;align-items:center;gap:10px}
#tpl-root .theme-toggle{display:flex;align-items:center;justify-content:center;width:32px;height:32px;border-radius:9px;border:1px solid var(--line);color:var(--muted);background:var(--paper);transition:all .2s}
#tpl-root .theme-toggle:hover{border-color:var(--accent);color:var(--accent)}
#tpl-root .btn-h{font-size:13px;font-weight:700;padding:7px 16px;border-radius:9px;transition:all .2s;font-family:var(--fb)}
#tpl-root .btn-h.primary{background:var(--accent);color:#fff;box-shadow:0 6px 18px rgba(99,102,241,.28)}
#tpl-root .btn-h.primary:hover{background:var(--accent-2);transform:translateY(-1px)}
#tpl-root .btn-h.ghost{border:1px solid var(--line);color:var(--muted)}
#tpl-root .btn-h.ghost:hover{border-color:rgba(99,102,241,.35);color:var(--ink)}

/* hero */
#tpl-root .hero{background:var(--paper);border-bottom:1px solid var(--line);padding:64px 0 52px;text-align:center;position:relative;overflow:hidden}
#tpl-root .hero::before{content:"";position:absolute;inset:0;background-image:radial-gradient(#e2e4f0 1px,transparent 1.4px);background-size:22px 22px;-webkit-mask-image:linear-gradient(#000 5%,transparent 75%);mask-image:linear-gradient(#000 5%,transparent 75%);pointer-events:none;opacity:.5}
#tpl-root[data-theme="dark"] .hero::before{background-image:radial-gradient(#252640 1px,transparent 1.4px)}
#tpl-root .hero .container{position:relative;z-index:1}
#tpl-root .hero-badge{display:inline-flex;align-items:center;gap:8px;font-size:12px;font-weight:600;letter-spacing:.04em;color:var(--accent);background:var(--soft);border:1px solid rgba(99,102,241,.22);border-radius:999px;padding:6px 14px;margin-bottom:20px}
#tpl-root .hero h1{font-family:var(--fd);font-weight:800;font-size:clamp(28px,5vw,50px);letter-spacing:-.04em;line-height:1.06;color:var(--ink);margin-bottom:14px}
#tpl-root .hero-sub{color:var(--muted);font-size:16px;max-width:520px;margin:0 auto 32px;line-height:1.6}
#tpl-root .hero-stats{display:flex;gap:32px;justify-content:center;flex-wrap:wrap}
#tpl-root .h-stat{text-align:center}
#tpl-root .h-stat-num{font-family:var(--fd);font-weight:800;font-size:26px;letter-spacing:-.03em;color:var(--ink)}
#tpl-root .h-stat-lbl{font-size:12px;color:var(--muted);margin-top:2px}

/* search + filters */
#tpl-root .controls{background:var(--bg);border-bottom:1px solid var(--line);padding:20px 0;position:sticky;top:62px;z-index:50}
#tpl-root .controls-in{display:flex;align-items:center;gap:16px;flex-wrap:wrap}
#tpl-root .search-wrap{position:relative;flex-shrink:0}
#tpl-root .search-ico{position:absolute;left:12px;top:50%;transform:translateY(-50%);color:var(--faint);pointer-events:none}
#tpl-root .search-input{padding:9px 14px 9px 36px;border:1px solid var(--line);border-radius:10px;font-size:13.5px;font-family:var(--fb);color:var(--ink);background:var(--paper);outline:none;width:220px;transition:border-color .2s}
#tpl-root .search-input:focus{border-color:var(--accent)}
#tpl-root .search-input::placeholder{color:var(--faint)}
#tpl-root .filters{display:flex;gap:6px;flex-wrap:wrap}
#tpl-root .filt-btn{font-size:13px;font-weight:600;padding:7px 14px;border-radius:20px;border:1px solid var(--line);color:var(--muted);background:var(--paper);transition:all .18s;cursor:pointer}
#tpl-root .filt-btn:hover{border-color:rgba(99,102,241,.35);color:var(--ink)}
#tpl-root .filt-btn.active{border-color:var(--accent);color:var(--accent);background:var(--soft)}
#tpl-root .count-badge{font-size:11px;font-weight:700;color:var(--faint);margin-left:auto;white-space:nowrap}

/* grid */
#tpl-root .main{padding:40px 0 80px}
#tpl-root .grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:20px}
#tpl-root .tcard{background:var(--paper);border:1px solid var(--line);border-radius:16px;overflow:hidden;cursor:pointer;transition:box-shadow .2s,transform .2s,border-color .2s;display:flex;flex-direction:column}
#tpl-root .tcard:hover{box-shadow:0 16px 48px rgba(99,102,241,.12);transform:translateY(-4px);border-color:rgba(99,102,241,.3)}
#tpl-root[data-theme="dark"] .tcard:hover{box-shadow:0 16px 48px rgba(0,0,0,.4)}

/* template preview area */
#tpl-root .tcard-thumb{height:180px;position:relative;overflow:hidden;display:flex;align-items:center;justify-content:center;border-bottom:1px solid var(--line)}
#tpl-root .tcard-bg{position:absolute;inset:0}
#tpl-root .tcard-icon{font-size:48px;line-height:1;position:relative;z-index:1;filter:drop-shadow(0 4px 12px rgba(0,0,0,.15))}
#tpl-root .tcard-new{position:absolute;top:10px;right:10px;font-size:10px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:#fff;background:var(--accent);border-radius:6px;padding:3px 8px;z-index:2}

/* wireframe overlay */
#tpl-root .wire{position:absolute;bottom:0;left:0;right:0;padding:10px 12px 0;display:flex;flex-direction:column;gap:5px;z-index:1;opacity:.55}
#tpl-root .w-bar{height:6px;border-radius:3px;background:rgba(255,255,255,.6)}
#tpl-root .w-bar.w80{width:80%}#tpl-root .w-bar.w60{width:60%}#tpl-root .w-bar.w40{width:40%}
#tpl-root .w-btn{height:16px;width:56px;border-radius:4px;background:rgba(255,255,255,.8)}
#tpl-root .w-row{display:flex;gap:5px}
#tpl-root .w-sq{border-radius:5px;background:rgba(255,255,255,.5)}

/* card body */
#tpl-root .tcard-body{padding:18px 18px 14px;flex:1;display:flex;flex-direction:column;gap:8px}
#tpl-root .tcard-top{display:flex;align-items:flex-start;justify-content:space-between;gap:8px}
#tpl-root .tcard-name{font-weight:800;font-size:15px;color:var(--ink);line-height:1.2}
#tpl-root .tcard-cat{font-size:11px;font-weight:600;color:var(--accent);background:var(--soft);border:1px solid rgba(99,102,241,.2);padding:2px 8px;border-radius:20px;white-space:nowrap;flex-shrink:0}
#tpl-root .tcard-desc{font-size:13px;color:var(--muted);line-height:1.5;flex:1}
#tpl-root .tcard-footer{display:flex;align-items:center;justify-content:space-between;padding-top:10px;border-top:1px solid var(--line);margin-top:4px}
#tpl-root .use-btn{font-size:13px;font-weight:700;color:var(--accent);display:flex;align-items:center;gap:5px;transition:gap .2s}
#tpl-root .tcard:hover .use-btn{gap:8px}
#tpl-root .styles-count{font-size:11.5px;color:var(--faint)}

/* empty state */
#tpl-root .empty{padding:80px 24px;text-align:center}
#tpl-root .empty-ico{font-size:48px;margin-bottom:16px}
#tpl-root .empty-title{font-size:18px;font-weight:700;color:var(--ink);margin-bottom:8px}
#tpl-root .empty-sub{font-size:14px;color:var(--muted)}

/* CTA bottom */
#tpl-root .cta-band{background:var(--paper);border-top:1px solid var(--line);padding:56px 0;text-align:center}
#tpl-root .cta-band h2{font-family:var(--fd);font-weight:800;font-size:clamp(22px,3.5vw,32px);letter-spacing:-.03em;color:var(--ink);margin-bottom:10px}
#tpl-root .cta-band p{font-size:15px;color:var(--muted);margin-bottom:24px}
#tpl-root .cta-btn{display:inline-flex;align-items:center;gap:8px;background:var(--accent);color:#fff;font-weight:700;font-size:14.5px;padding:13px 28px;border-radius:12px;border:0;cursor:pointer;font-family:var(--fb);transition:all .22s;box-shadow:0 10px 28px rgba(99,102,241,.3)}
#tpl-root .cta-btn:hover{background:var(--accent-2);transform:translateY(-2px)}

/* footer */
#tpl-root footer{background:var(--bg);border-top:1px solid var(--line);padding:28px 0}
#tpl-root .foot-in{display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:16px}
#tpl-root .foot-links{display:flex;gap:6px;flex-wrap:wrap}
#tpl-root .foot-link{font-size:13px;color:var(--muted);padding:5px 10px;border-radius:8px;transition:all .15s;cursor:pointer}
#tpl-root .foot-link:hover{color:var(--ink);background:var(--paper)}
#tpl-root .foot-copy{font-size:12.5px;color:var(--faint)}

@media(max-width:640px){
  #tpl-root .controls-in{gap:12px}
  #tpl-root .search-input{width:180px}
  #tpl-root .grid{grid-template-columns:1fr 1fr}
  #tpl-root .hero-stats{gap:20px}
  #tpl-root .h-stat-num{font-size:20px}
}
@media(max-width:400px){
  #tpl-root .grid{grid-template-columns:1fr}
}
`

// ── Template data ────────────────────────────────────────────────────────────

type Category = 'all' | 'landing' | 'portfolio' | 'store' | 'services' | 'business' | 'content'

interface TplMeta {
  id: string
  label: string
  description: string
  icon: string
  category: Category
  styles?: number
  isNew?: boolean
  accentColor: string
}

const TEMPLATES: TplMeta[] = [
  // Лендинг
  { id: 'landing',      label: 'Лендинг',                   description: 'Продающая страница с преимуществами, отзывами и формой',             icon: '🚀', category: 'landing',   styles: 9,  accentColor: '#6366f1' },
  { id: 'startup',      label: 'Стартап',                   description: 'Hero + статистика + цены + команда. Идеально для продуктового запуска', icon: '💡', category: 'landing',   styles: 4,  accentColor: '#8b5cf6' },
  { id: 'saas',         label: 'SaaS Продукт',              description: 'Лендинг для SaaS-приложения с демо, тарифами и FAQ',                  icon: '☁️', category: 'landing',   isNew: true, accentColor: '#3b82f6' },
  { id: 'nonprofit',    label: 'НКО / Благотворительность', description: 'Сайт для некоммерческой организации или фонда',                       icon: '❤️', category: 'landing',   isNew: true, accentColor: '#ef4444' },

  // Портфолио
  { id: 'portfolio',    label: 'Портфолио',                 description: 'Покажите свои работы и навыки с masonry-галереей',                    icon: '🎨', category: 'portfolio', styles: 8,  accentColor: '#ec4899' },
  { id: 'photography',  label: 'Фотостудия',                description: 'Сайт для фотографа или фотостудии с галереей и прайсом',              icon: '📸', category: 'portfolio', isNew: true, accentColor: '#1f2937' },
  { id: 'music',        label: 'Музыкант / Группа',         description: 'Сайт для музыкантов, групп и исполнителей с треками',                 icon: '🎵', category: 'portfolio', isNew: true, accentColor: '#7c3aed' },
  { id: 'wedding',      label: 'Свадебная фотография',      description: 'Сайт свадебного фотографа или агентства',                             icon: '💒', category: 'portfolio', isNew: true, accentColor: '#f9a8d4' },

  // Магазин
  { id: 'store',        label: 'Интернет-магазин',          description: 'Каталог, преимущества, форма заказа и корзина',                       icon: '🛒', category: 'store',     styles: 4,  accentColor: '#10b981' },
  { id: 'food-delivery',label: 'Доставка еды',              description: 'Сайт сервиса доставки еды или тёмной кухни',                          icon: '🍕', category: 'store',     isNew: true, accentColor: '#f59e0b' },

  // Услуги
  { id: 'restaurant',   label: 'Ресторан / Кафе',           description: 'Меню, бронирование столика, галерея и карта заведения',               icon: '🍽', category: 'services',  styles: 4,  accentColor: '#f59e0b' },
  { id: 'fitness',      label: 'Фитнес / Спорт',            description: 'Студия, тренажёрный зал или персональный тренер',                     icon: '💪', category: 'services',  accentColor: '#ef4444' },
  { id: 'beauty',       label: 'Красота / Салон',           description: 'Салон красоты, барбершоп, косметолог или мастер ногтей',              icon: '💅', category: 'services',  accentColor: '#ec4899' },
  { id: 'clinic',       label: 'Медицина / Клиника',        description: 'Клиника, стоматология, психолог или врач частной практики',           icon: '🏥', category: 'services',  accentColor: '#0ea5e9' },
  { id: 'dentist',      label: 'Стоматология',              description: 'Сайт стоматологической клиники с услугами и записью',                 icon: '🦷', category: 'services',  isNew: true, accentColor: '#06b6d4' },
  { id: 'barber',       label: 'Барбершоп',                 description: 'Сайт для барбершопа или мужской парикмахерской',                      icon: '✂️', category: 'services',  isNew: true, accentColor: '#1f2937' },
  { id: 'yoga',         label: 'Йога / Wellness студия',    description: 'Сайт для йоги, пилатеса, медитации и wellness-практик',               icon: '🧘', category: 'services',  isNew: true, accentColor: '#a78bfa' },
  { id: 'pet',          label: 'Ветеринария / Зоотовары',   description: 'Ветеринарная клиника, зоомагазин или груминг-салон',                  icon: '🐾', category: 'services',  accentColor: '#84cc16' },
  { id: 'event',        label: 'Мероприятие / Конференция', description: 'Конференция, форум, выставка или концерт с программой',               icon: '🎤', category: 'services',  accentColor: '#f97316' },

  // Бизнес
  { id: 'it-agency',    label: 'IT-агентство / Студия',     description: 'Веб-студия, IT-компания или digital-агентство с портфолио',           icon: '💻', category: 'business',  accentColor: '#6366f1' },
  { id: 'consulting',   label: 'Консалтинг / Коучинг',      description: 'Бизнес-консультант, коуч или эксперт с кейсами и отзывами',           icon: '💼', category: 'business',  accentColor: '#0891b2' },
  { id: 'legal',        label: 'Юридические услуги',        description: 'Адвокат, юридическая фирма или нотариус',                             icon: '⚖️', category: 'business',  accentColor: '#374151' },
  { id: 'accounting',   label: 'Бухгалтерия / Финансы',     description: 'Бухгалтерские услуги, аудит или финансовый консультант',              icon: '📊', category: 'business',  accentColor: '#047857' },
  { id: 'construction', label: 'Строительство / Ремонт',    description: 'Строительная компания, ремонт квартир или дизайн интерьера',          icon: '🏗️', category: 'business',  accentColor: '#b45309' },
  { id: 'real-estate',  label: 'Недвижимость',              description: 'Агентство недвижимости, риелтор или застройщик',                      icon: '🏠', category: 'business',  accentColor: '#2563eb' },
  { id: 'travel',       label: 'Туризм / Путешествия',      description: 'Туристическое агентство, гид или туроператор с турами',               icon: '✈️', category: 'business',  isNew: true, accentColor: '#0284c7' },
  { id: 'logistics',    label: 'Логистика / Грузоперевозки', description: 'Сайт для транспортной или логистической компании',                  icon: '🚛', category: 'business',  isNew: true, accentColor: '#d97706' },

  // Контент
  { id: 'blog',         label: 'Блог / Медиа',              description: 'Статьи, подписка, социальные ссылки и RSS-лента',                    icon: '📝', category: 'content',   accentColor: '#6366f1' },
  { id: 'education',    label: 'Онлайн-школа / Курсы',      description: 'Онлайн-курсы, репетитор или образовательный центр',                  icon: '🎓', category: 'content',   accentColor: '#7c3aed' },
]

const CATEGORIES: { id: Category | 'all'; label: string }[] = [
  { id: 'all',       label: 'Все' },
  { id: 'landing',   label: 'Лендинг' },
  { id: 'portfolio', label: 'Портфолио' },
  { id: 'store',     label: 'Магазин' },
  { id: 'services',  label: 'Услуги' },
  { id: 'business',  label: 'Бизнес' },
  { id: 'content',   label: 'Контент' },
]

// ── Template card ────────────────────────────────────────────────────────────

function TemplateCard({ tpl, onUse }: { tpl: TplMeta; onUse: () => void }) {
  const [hovered, setHovered] = useState(false)
  const bg = tpl.accentColor

  return (
    <div
      className="tcard"
      onClick={onUse}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="tcard-thumb">
        {/* Gradient background */}
        <div
          className="tcard-bg"
          style={{ background: `linear-gradient(145deg, ${bg}26 0%, ${bg}0d 100%)` }}
        />
        {/* Mini wireframe */}
        <div className="wire">
          <div className="w-bar w80" style={{ background: `${bg}55` }} />
          <div className="w-bar w60" style={{ background: `${bg}40` }} />
          <div style={{ display:'flex', gap:5, marginTop:2 }}>
            <div className="w-btn" style={{ background: `${bg}70` }} />
            <div className="w-btn" style={{ background: `${bg}30`, width:40 }} />
          </div>
          <div className="w-row" style={{ marginTop:6, gap:5 }}>
            {[0,1,2].map(i => (
              <div key={i} className="w-sq" style={{ flex:1, height:46, background:`${bg}25` }} />
            ))}
          </div>
        </div>
        {/* Icon */}
        <div className="tcard-icon" style={{ transform: hovered ? 'scale(1.08) translateY(-4px)' : 'none', transition: 'transform .2s' }}>
          {tpl.icon}
        </div>
        {tpl.isNew && <span className="tcard-new">Новый</span>}
      </div>

      <div className="tcard-body">
        <div className="tcard-top">
          <div className="tcard-name">{tpl.label}</div>
          <span className="tcard-cat">
            {CATEGORIES.find(c => c.id === tpl.category)?.label ?? ''}
          </span>
        </div>
        <div className="tcard-desc">{tpl.description}</div>
        <div className="tcard-footer">
          <span className="use-btn">
            Использовать
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
          </span>
          {tpl.styles ? (
            <span className="styles-count">{tpl.styles} стилей</span>
          ) : null}
        </div>
      </div>
    </div>
  )
}

// ── TemplatesPage ────────────────────────────────────────────────────────────

export function TemplatesPage() {
  const navigate = useNavigate()
  const [theme, setTheme] = useState<Theme>('light')
  const [scrolled, setScrolled] = useState(false)
  const [activeCat, setActiveCat] = useState<Category | 'all'>('all')
  const [query, setQuery] = useState('')

  useEffect(() => {
    const saved = localStorage.getItem('bst-lp-theme') as Theme | null
    if (saved === 'dark' || saved === 'light') setTheme(saved)
    else if (window.matchMedia('(prefers-color-scheme: dark)').matches) setTheme('dark')
    document.body.style.overflow = 'auto'
    document.documentElement.style.overflow = 'auto'
    document.body.style.height = 'auto'
    document.documentElement.classList.remove('dark')
    return () => {
      document.body.style.overflow = ''
      document.documentElement.style.overflow = ''
      document.body.style.height = ''
    }
  }, [])

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const toggleTheme = () => setTheme(t => {
    const next: Theme = t === 'light' ? 'dark' : 'light'
    localStorage.setItem('bst-lp-theme', next)
    return next
  })

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim()
    return TEMPLATES.filter(t => {
      const matchCat = activeCat === 'all' || t.category === activeCat
      const matchQ = !q || t.label.toLowerCase().includes(q) || t.description.toLowerCase().includes(q)
      return matchCat && matchQ
    })
  }, [activeCat, query])

  const useTemplate = (id: string) => navigate(`/login?template=${id}`)

  const totalStyles = TEMPLATES.reduce((acc, t) => acc + (t.styles ?? 1), 0)

  return (
    <div id="tpl-root" data-theme={theme}>
      <style>{TPL_CSS}</style>

      {/* ── Header ── */}
      <header className={scrolled ? 'scrolled' : ''}>
        <div className="container h-in">
          <div className="brand" onClick={() => navigate('/')}>
            <span className="brand-mark">B</span>
            <span className="brand-name">BST</span>
          </div>
          <span className="sep">/</span>
          <span className="crumb">Шаблоны</span>
          <div className="h-right">
            <button className="theme-toggle" onClick={toggleTheme} aria-label="Toggle theme">
              {theme === 'light'
                ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="4.5"/><path d="M12 2v2M12 20v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M2 12h2M20 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>
                : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79z"/></svg>
              }
            </button>
            <button className="btn-h ghost" onClick={() => navigate('/')}>← Главная</button>
            <button className="btn-h primary" onClick={() => navigate('/login')}>Создать сайт</button>
          </div>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="hero">
        <div className="container">
          <div className="hero-badge">29 профессиональных шаблонов</div>
          <h1>Начните с готового шаблона</h1>
          <p className="hero-sub">
            Каждый шаблон полностью редактируется — тексты, цвета, блоки, изображения.
            Сайт готов к публикации через 5 минут.
          </p>
          <div className="hero-stats">
            <div className="h-stat">
              <div className="h-stat-num">29</div>
              <div className="h-stat-lbl">шаблонов</div>
            </div>
            <div className="h-stat">
              <div className="h-stat-num">{totalStyles}+</div>
              <div className="h-stat-lbl">вариантов стиля</div>
            </div>
            <div className="h-stat">
              <div className="h-stat-num">376+</div>
              <div className="h-stat-lbl">готовых блоков</div>
            </div>
            <div className="h-stat">
              <div className="h-stat-num">7</div>
              <div className="h-stat-lbl">категорий</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Controls ── */}
      <div className="controls">
        <div className="container controls-in">
          <div className="search-wrap">
            <svg className="search-ico" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></svg>
            <input
              className="search-input"
              type="text"
              placeholder="Поиск шаблона..."
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </div>
          <div className="filters">
            {CATEGORIES.map(cat => (
              <button
                key={cat.id}
                className={`filt-btn${activeCat === cat.id ? ' active' : ''}`}
                onClick={() => { setActiveCat(cat.id as Category | 'all'); setQuery('') }}
              >
                {cat.label}
              </button>
            ))}
          </div>
          <span className="count-badge">{filtered.length} шаблонов</span>
        </div>
      </div>

      {/* ── Grid ── */}
      <div className="main">
        <div className="container">
          {filtered.length === 0 ? (
            <div className="empty">
              <div className="empty-ico">🔍</div>
              <div className="empty-title">Ничего не найдено</div>
              <div className="empty-sub">Попробуйте другой запрос или выберите другую категорию</div>
            </div>
          ) : (
            <div className="grid">
              {filtered.map(tpl => (
                <TemplateCard key={tpl.id} tpl={tpl} onUse={() => useTemplate(tpl.id)} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── CTA band ── */}
      <div className="cta-band">
        <div className="container">
          <h2>Не нашли подходящий?</h2>
          <p>Начните с чистого холста — выберите нужные блоки из библиотеки 376+ элементов.</p>
          <button className="cta-btn" onClick={() => navigate('/login')}>
            Создать с нуля
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
          </button>
        </div>
      </div>

      {/* ── Footer ── */}
      <footer>
        <div className="container foot-in">
          <div className="brand" onClick={() => navigate('/')}>
            <span className="brand-mark">B</span>
            <span className="brand-name">BST</span>
          </div>
          <nav className="foot-links">
            <span className="foot-link" onClick={() => navigate('/')}>Главная</span>
            <span className="foot-link" onClick={() => navigate('/showcase')}>Витрина</span>
            <span className="foot-link" onClick={() => navigate('/for/restaurants')}>Для ресторанов</span>
            <span className="foot-link" onClick={() => navigate('/for/agencies')}>Для агентств</span>
            <span className="foot-link" onClick={() => navigate('/for/freelancers')}>Для фрилансеров</span>
            <span className="foot-link" onClick={() => navigate('/for/startups')}>Для стартапов</span>
            <span className="foot-link" onClick={() => navigate('/changelog')}>Changelog</span>
          </nav>
          <span className="foot-copy">© 2026 BST</span>
        </div>
      </footer>
    </div>
  )
}
